import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import projectService from "../services/projectService";
import authService from "../services/authService";
import "../styles/Projects.css";

function AvailableProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRequests, setUserRequests] = useState([]);
  const [licenseStatus, setLicenseStatus] = useState({
    valid: false,
    expiry: null,
    message: "Checking license..."
  });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [user, projectsData] = await Promise.all([
          authService.getMe(),
          projectService.fetchAvailableProjects()
        ]);

        setCurrentUser(user);
        setProjects(projectsData);

        if (user) {
          // Check License
          if (!user.licenseExpiry) {
            setLicenseStatus({
              valid: false,
              expiry: null,
              message: "No active license found. Please contact admin."
            });
          } else {
            const expiryDate = new Date(user.licenseExpiry);
            const now = new Date();
            const isValid = expiryDate > now;

            setLicenseStatus({
              valid: isValid,
              expiry: user.licenseExpiry,
              message: isValid ? "License Active" : "License Expired"
            });
          }

          // Fetch user requests to see what they've already applied for
          const requests = await projectService.fetchUserRequests(user.uid);
          setUserRequests(requests);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleGrabProject = async (projectId) => {
    if (!currentUser || !licenseStatus.valid) return;

    try {
      await projectService.requestProject(projectId, currentUser.uid, currentUser.name);
      alert("Request sent to admin! Wait for verification.");

      // Refresh requests
      const requests = await projectService.fetchUserRequests(currentUser.uid);
      setUserRequests(requests);
    } catch (error) {
      console.error("Error requesting project:", error);
      alert("Failed to send request.");
    }
  };

  const isRequested = (projectId) => {
    return userRequests.some(req => req.projectId === projectId);
  };

  return (
    <div className="projects-page">
      <Sidebar />
      <div className="projects-container">
        <h2 className="page-title">Available Projects (Marketplace)</h2>
        <p className="page-subtitle">Browse and grab available projects. Admin will verify your request.</p>

        {!loading && !licenseStatus.valid ? (
          <div className="license-alert" style={{
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
            marginTop: '2rem'
          }}>
            <h3 style={{ color: '#cf1322', marginBottom: '1rem' }}>⚠️ Access Restricted</h3>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{licenseStatus.message}</p>
            {licenseStatus.expiry && <p>Expired on: {new Date(licenseStatus.expiry).toLocaleDateString()}</p>}
            <p style={{ marginTop: '1rem', fontWeight: '500' }}>Please renew your subscription to access the marketplace.</p>
          </div>
        ) : loading ? (
          <p>Loading projects...</p>
        ) : (
          <div className="projects-grid">
            {projects.length === 0 ? (
              <p>No projects available at the moment.</p>
            ) : (
              projects.map((project) => (
                <div key={project._id} className="project-card">
                  {project.image && (
                    <img src={project.image} alt={project.title} className="project-image" />
                  )}
                  <div className="project-content">
                    <h3>{project.title}</h3>
                    <p className="project-desc">{project.description}</p>

                    <div className="project-meta">
                      <span>💰 ₹{project.budget?.toLocaleString()}</span>
                      <span>📅 {project.deadline}</span>
                    </div>

                    {project.skills && project.skills.length > 0 && (
                      <div className="project-skills">
                        {project.skills.map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    )}

                    <button
                      className="btn-apply"
                      disabled={isRequested(project._id)}
                      onClick={() => handleGrabProject(project._id)}
                      style={{
                        background: isRequested(project._id) ? '#faad14' : 'var(--accent-primary)',
                        cursor: isRequested(project._id) ? 'default' : 'pointer'
                      }}
                    >
                      {isRequested(project._id) ? "Request Pending" : "Grab Project"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AvailableProjects;
